// This surface has three separate status vocabularies: the list filter, the
// create/update body enum, and the console's own map (which keys on APPROVED).
// This file is the Enterprise API's own vocabulary only (e.g. ENABLED, not
// APPROVED) — do not copy from the console's map, and do not share a mapper
// across the three.
//
// A template's Meta quality rating (RED/GREEN/YELLOW, or absent) is a fourth,
// fully independent axis on top of those three. Lifecycle status and quality
// score vary separately — an ENABLED template can carry any rating, or none —
// so don't conflate the two when deciding what a card should render.

export const TEMPLATE_STATUS = Object.freeze({
  ENABLED: { label: 'Approved', tone: 'success' },
  PENDING: { label: 'Pending', tone: 'warning' },
  PENDING_APPROVAL: { label: 'Pending', tone: 'warning' },
  REJECTED: { label: 'Rejected', tone: 'error' },
  DISABLED: { label: 'Disabled', tone: 'neutral' },
  WHATSAPP_DISABLED: { label: 'Disabled by WhatsApp', tone: 'neutral' },
  PAUSED: { label: 'Paused', tone: 'neutral' },
  ARCHIVED: { label: 'Archived', tone: 'neutral' },
});

// Ordered as the list screen's filter rail renders them. Only values the list
// endpoint accepts; the server defaults to ENABLED when the param is omitted.
export const STATUS_FILTERS = ['ENABLED', 'PENDING', 'REJECTED', 'DISABLED', 'ARCHIVED', 'ALL'];

export const DEFAULT_STATUS_FILTER = 'ENABLED';

// Upstream (MessageTemplate.java:1103-1113) only permits an update in these three states.
export const EDITABLE_STATUSES = Object.freeze(['ENABLED', 'REJECTED', 'PAUSED']);

// Never returns undefined — the list table renders this directly.
export function statusMeta(status) {
  return TEMPLATE_STATUS[status] || { label: String(status ?? 'Unknown'), tone: 'neutral' };
}

export function isEditable(status) {
  return EDITABLE_STATUSES.includes(status);
}

// This file's own tone vocabulary (success/warning/error/neutral, see TEMPLATE_STATUS/
// QUALITY_RATINGS above) mapped to MUI Chip's `color` prop, which has no "neutral" — shared by
// List.js and DetailModal.js so both cards/detail chips render from the same lookup.
export const TONE_TO_CHIP_COLOR = { success: 'success', warning: 'warning', error: 'error', neutral: 'default' };

// gs-ui-templates console's own equivalent of APPROVED on our wire. CardHeader.tsx:120 hides the
// status chip with `template.status !== templateStatus.approved` — this is that comparison value.
export const APPROVED_STATUS_VALUE = 'ENABLED';

// gs-ui-templates CardHeader.tsx:126 only renders the quality chip when
// `TEMPLATE_VALID_STATUS.includes(template.status)` (constants/common.ts). This is that same
// list translated to our wire's status vocabulary.
export const QUALITY_VISIBLE_STATUSES = Object.freeze(['ENABLED', 'PAUSED']);

// Mirrors gs-ui-templates constants/common.ts TEMPLATE_QUALITY_RATINGS, keyed by Meta's own
// quality_score.score values. Verified pass-through, no transformation, in
// ce-api/whatsappmediaapi TemplateUtils.java:215 — our wire carries the same
// GREEN/YELLOW/RED/UNKNOWN(or absent) domain Meta sends. Tones reuse this file's own
// success/warning/error/neutral vocabulary (see TEMPLATE_STATUS above) — not a second one.
export const QUALITY_RATINGS = Object.freeze({
  RED: { label: 'Low', tone: 'error' },
  GREEN: { label: 'Good', tone: 'success' },
  YELLOW: { label: 'Average', tone: 'warning' },
});

// Never returns undefined, same pattern as statusMeta. Mirrors the console's DEFAULT_RATING
// fallback (constants/common.ts) for a quality value that's missing or not one of the three
// known ratings.
export function qualityMeta(quality) {
  return QUALITY_RATINGS[quality] || { label: 'Not Rated', tone: 'neutral' };
}
